/**
 * Enhanced Authentication Hook
 * 
 * This hook provides a comprehensive way to access authentication state
 * and methods throughout the application, with enhanced permission management.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth as useAuthContext } from '../components/auth/AuthProvider';
import { User, Permission } from '../types';

interface UseAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  isLoggedIn: boolean;
  user: User | null;
  permissions: Permission[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  
  // User information
  isAdmin: boolean;
  isSuperUser: boolean;
  isVerified: boolean;
  userName: string;
  userEmail: string;
  userId: number | null;
  
  // Session management
  lastActivity: number;
  sessionTimeout: number | null;
  updateActivity: () => void;
  checkSessionTimeout: () => boolean;
  resetSessionTimeout: () => void;
  
  // Permission checks
  canAccessValueChain: (valueChainId: string, level?: string) => boolean;
  canAccessCountry: (countryCode: string, level?: string) => boolean;
  canAccessData: (dataType: string, level?: string) => boolean;
  hasAnyValueChainAccess: (level?: string) => boolean;
  hasAnyCountryAccess: (level?: string) => boolean;
  hasAnyDataAccess: (level?: string) => boolean;
  
  // Permission management
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  canAccessResource: (resource: { type: string; id: string }, level?: string) => boolean;
  canAccessAnyResource: (resources: Array<{ type: string; id: string }>, level?: string) => boolean;
  
  // Authentication actions
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; error?: string; totp_required?: boolean }>;
  logout: () => void;
  register: (userData: any) => Promise<{ success: boolean; message?: string; error?: string }>;
  
  // Permission utilities
  getAccessibleValueChains: (level?: string) => string[];
  getAccessibleCountries: (level?: string) => string[];
  getAccessibleDataTypes: (level?: string) => string[];
  getPermissionSummary: () => any;
  getAccessibleDataPermissions: () => any[];
}

/**
 * Custom hook for authentication with enhanced features
 * @returns Authentication state and methods
 */
export const useAuth = (): UseAuthReturn => {
  const auth = useAuthContext();
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [sessionTimeout, setSessionTimeout] = useState<number | null>(null);
  
  // Add computed properties
  const isLoggedIn = auth.isAuthenticated && !!auth.user;
  const isAdmin = auth.user?.role === 'admin' || auth.user?.role === 'superuser';
  const isSuperUser = auth.user?.role === 'superuser';
  const isVerified = auth.user?.is_verified || false;
  const userName = auth.user?.full_name || auth.user?.email || 'User';
  const userEmail = auth.user?.email || '';
  const userId = auth.user?.id || null;
  
  // Session management
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  const checkSessionTimeout = useCallback((): boolean => {
    if (!sessionTimeout) return false;
    return Date.now() - lastActivity > sessionTimeout;
  }, [lastActivity, sessionTimeout]);

  const resetSessionTimeout = useCallback(() => {
    setSessionTimeout(null);
  }, []);

  // Permission checks
  const canAccessValueChain = useCallback((valueChainId: string, level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.valueChains.some(perm => 
      perm.value_chain_id === valueChainId &&
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  const canAccessCountry = useCallback((countryCode: string, level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.countries.some(perm => 
      perm.country_code === countryCode &&
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  const canAccessData = useCallback((dataType: string, level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.dataPermissions.some(perm => 
      perm.data_type === dataType &&
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  const hasAnyValueChainAccess = useCallback((level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.valueChains.some(perm => 
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  const hasAnyCountryAccess = useCallback((level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.countries.some(perm => 
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  const hasAnyDataAccess = useCallback((level: string = 'read'): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.dataPermissions.some(perm => 
      perm.is_active &&
      (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
      (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
    );
  }, [isLoggedIn, auth.permissions]);

  // Permission management
  const hasPermission = useCallback((permission: string): boolean => {
    if (!isLoggedIn || !auth.permissions) return false;
    
    // Map permission to resource checks
    const permissionMap: Record<string, () => boolean> = {
      'view_value_chain': () => hasAnyValueChainAccess('read'),
      'edit_value_chain': () => hasAnyValueChainAccess('write'),
      'admin_value_chain': () => hasAnyValueChainAccess('admin'),
      'view_country': () => hasAnyCountryAccess('read'),
      'edit_country': () => hasAnyCountryAccess('write'),
      'admin_country': () => hasAnyCountryAccess('admin'),
      'view_data': () => hasAnyDataAccess('read'),
      'edit_data': () => hasAnyDataAccess('write'),
      'admin_data': () => hasAnyDataAccess('admin'),
    };
    
    return permissionMap[permission] ? permissionMap[permission]() : false;
  }, [isLoggedIn, auth.permissions, hasAnyValueChainAccess, hasAnyCountryAccess, hasAnyDataAccess]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!isLoggedIn) return false;
    return permissions.some(permission => hasPermission(permission));
  }, [isLoggedIn, hasPermission]);

  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    if (!isLoggedIn) return false;
    return permissions.every(permission => hasPermission(permission));
  }, [isLoggedIn, hasPermission]);

  const canAccessResource = useCallback((resource: { type: string; id: string }, level: string = 'read'): boolean => {
    if (!isLoggedIn) return false;
    
    switch (resource.type) {
      case 'value_chain':
        return canAccessValueChain(resource.id, level);
      case 'country':
        return canAccessCountry(resource.id, level);
      case 'data':
        return canAccessData(resource.id, level);
      default:
        return false;
    }
  }, [isLoggedIn, canAccessValueChain, canAccessCountry, canAccessData]);

  const canAccessAnyResource = useCallback((resources: Array<{ type: string; id: string }>, level: string = 'read'): boolean => {
    if (!isLoggedIn) return false;
    return resources.some(resource => canAccessResource(resource, level));
  }, [isLoggedIn, canAccessResource]);

  // Permission utilities
  const getAccessibleValueChains = useCallback((level: string = 'read'): string[] => {
    if (!isLoggedIn || !auth.permissions) return [];
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.valueChains
      .filter(perm => 
        perm.is_active &&
        (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
        (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
      )
      .map(perm => perm.value_chain_id);
  }, [isLoggedIn, auth.permissions]);

  const getAccessibleCountries = useCallback((level: string = 'read'): string[] => {
    if (!isLoggedIn || !auth.permissions) return [];
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.countries
      .filter(perm => 
        perm.is_active &&
        (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
        (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
      )
      .map(perm => perm.country_code);
  }, [isLoggedIn, auth.permissions]);

  const getAccessibleDataTypes = useCallback((level: string = 'read'): string[] => {
    if (!isLoggedIn || !auth.permissions) return [];
    
    const requiredLevel = level === 'admin' ? 3 : level === 'write' ? 2 : 1;
    return auth.permissions.dataPermissions
      .filter(perm => 
        perm.is_active &&
        (!perm.expires_at || new Date(perm.expires_at) > new Date()) &&
        (perm.permission_level === 'admin' ? 3 : perm.permission_level === 'write' ? 2 : 1) >= requiredLevel
      )
      .map(perm => perm.data_type);
  }, [isLoggedIn, auth.permissions]);

  const getPermissionSummary = useCallback(() => {
    if (!auth.permissions) return { total: 0, byType: {}, byLevel: {} };
    
    const allPermissions = [
      ...auth.permissions.valueChains,
      ...auth.permissions.countries,
      ...auth.permissions.dataPermissions
    ];
    
    const total = allPermissions.length;
    const byType = allPermissions.reduce((acc, perm) => {
      const type = 'value_chain' in perm ? 'value_chain' : 
                   'country_code' in perm ? 'country' : 'data';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const byLevel = allPermissions.reduce((acc, perm) => {
      acc[perm.permission_level] = (acc[perm.permission_level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return { total, byType, byLevel };
  }, [auth.permissions]);

  const getAccessibleDataPermissions = useCallback(() => {
    if (!auth.permissions) return [];
    return auth.permissions.dataPermissions;
  }, [auth.permissions]);

  // Update activity on user interaction
  useEffect(() => {
    const handleUserActivity = () => {
      updateActivity();
    };

    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    return () => {
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [updateActivity]);

  return {
    // Authentication state
    isAuthenticated: auth.isAuthenticated,
    isLoggedIn,
    user: auth.user,
    permissions: auth.permissions ? [
      ...auth.permissions.valueChains,
      ...auth.permissions.countries,
      ...auth.permissions.dataPermissions
    ] : [],
    loading: auth.isLoading,
    error: auth.error,
    initialized: auth.initialized,
    
    // User information
    isAdmin,
    isSuperUser,
    isVerified,
    userName,
    userEmail,
    userId,
    
    // Session management
    lastActivity,
    sessionTimeout,
    updateActivity,
    checkSessionTimeout,
    resetSessionTimeout,
    
    // Permission checks
    canAccessValueChain,
    canAccessCountry,
    canAccessData,
    hasAnyValueChainAccess,
    hasAnyCountryAccess,
    hasAnyDataAccess,
    
    // Permission management
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessResource,
    canAccessAnyResource,
    
    // Authentication actions
    login: auth.login,
    logout: auth.logout,
    register: auth.register,
    
    // Permission utilities
    getAccessibleValueChains,
    getAccessibleCountries,
    getAccessibleDataTypes,
    getPermissionSummary,
    getAccessibleDataPermissions,
  };
};
